// vim: set ts=2 sts=2 sw=2 et:

import React from "react";

import { BarLoadView } from "../../components/lifting/BarLoad";
import type { Equipment } from "../../types/dataTypes";
import { canUseBrowserWebSocket, getBrowserPlatformLiveWebSocketUrl } from "../live/browserPlatformLiveClient";
import {
  JudgeLights,
  JudgeLightValue,
  PlatformLiveAttempt,
  PlatformLiveAttemptClock,
  PlatformLiveResolvedAttempt,
  PlatformLiveServerMessage,
  PlatformLiveState,
} from "../live/platformLiveProtocol";
import FitLine from "../live/FitLine";
import { judgeLightsShownTogether } from "../live/judgeLightsShownTogether";
import { getEffectiveServerNowMs, getRemainingClockSeconds } from "../live/platformLiveClock";
import { JUDGE_POSITIONS, JudgePosition } from "../judging/resolveJudgeVotes";
import { recordAttemptAriaLabel, recordAttemptLabel, recordCategoryDataAttributes } from "../records/recordCategories";
import { SpectatorContainerProps } from "./SpectatorContainer";
import FitToHeight from "./FitToHeight";
import SpectatorLiftProgress from "./SpectatorLiftProgress";
import spectatorBackground from "./spectator-background.jpeg";
import sponsors from "../streaming/broadcast/assets/sponsors-iberoamericano-2026.png";

import styles from "./SpectatorDisplay.module.scss";

interface SpectatorState {
  status: "connecting" | "connected" | "waiting" | "rejected" | "reconnecting";
  error: string | null;
  operatorConnected: boolean;
  currentAttempt: PlatformLiveAttempt | null;
  nextAttempt: PlatformLiveAttempt | null;
  lastResolvedAttempt: PlatformLiveResolvedAttempt | null;
  attemptClock: PlatformLiveAttemptClock | null;
  judgeLights: JudgeLights;
  manualVerdictConfirmation: boolean;
  serverNowMs: number | null;
  clockReceivedAtMs: number | null;
  clockTick: number;
  viewportWidth: number;
  viewportHeight: number;
}

const REFERENCE_VIEWPORT_WIDTH = 1920;
const REFERENCE_VIEWPORT_HEIGHT = 1080;

const getViewportSize = () => ({
  viewportWidth: Math.max(1, window.innerWidth),
  viewportHeight: Math.max(1, window.innerHeight),
});

const emptyJudgeLights = (): JudgeLights => ({ J1: "off", J2: "off", J3: "off" });

// Alto de `.barLoadGraphic` en SpectatorDisplay.module.scss.
const BAR_LOAD_GRAPHIC_HEIGHT_PX = 258;

const makeEmptyDisplayState = () => ({
  operatorConnected: false,
  currentAttempt: null,
  nextAttempt: null,
  lastResolvedAttempt: null,
  attemptClock: null,
  judgeLights: emptyJudgeLights(),
  manualVerdictConfirmation: false,
  serverNowMs: null,
  clockReceivedAtMs: null,
  clockTick: Date.now(),
});

class SpectatorView extends React.Component<SpectatorContainerProps, SpectatorState> {
  private socket: WebSocket | null = null;
  private clockTimer: number | null = null;
  private reconnectTimer: number | null = null;
  private reconnectAttempts = 0;

  constructor(props: SpectatorContainerProps) {
    super(props);
    this.state = {
      status: "connecting",
      error: null,
      ...makeEmptyDisplayState(),
      ...getViewportSize(),
    };
  }

  componentDidMount() {
    window.addEventListener("resize", this.syncViewportSize);
    this.connect();
  }

  componentDidUpdate(prevProps: SpectatorContainerProps) {
    if (
      prevProps.locationSearch !== this.props.locationSearch ||
      prevProps.platformRef?.day !== this.props.platformRef?.day ||
      prevProps.platformRef?.platformId !== this.props.platformRef?.platformId
    ) {
      this.disconnect();
      this.connect();
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.syncViewportSize);
    this.disconnect();
    this.stopClockTicker();
  }

  syncViewportSize = () => {
    this.setState(getViewportSize());
  };

  getCanvasStyle = (): React.CSSProperties => {
    const { viewportWidth, viewportHeight } = this.state;
    const scale = Math.min(viewportWidth / REFERENCE_VIEWPORT_WIDTH, viewportHeight / REFERENCE_VIEWPORT_HEIGHT);
    return {
      height: `${viewportHeight / scale}px`,
      transform: `scale(${scale})`,
      width: `${viewportWidth / scale}px`,
    };
  };

  connect = () => {
    if (this.props.platformRef === null) {
      this.setState({ status: "rejected", error: "Invalid Spectator URL.", ...makeEmptyDisplayState() });
      return;
    }

    if (!canUseBrowserWebSocket()) {
      this.setState({ status: "waiting", error: "Browser WebSocket is unavailable.", ...makeEmptyDisplayState() });
      return;
    }

    const socket = new window.WebSocket(
      getBrowserPlatformLiveWebSocketUrl(this.props.platformRef, this.props.locationSearch),
    );
    this.socket = socket;
    this.setState({ status: "connecting", error: null, ...makeEmptyDisplayState() });

    socket.onopen = () => {
      if (this.socket !== socket) {
        return;
      }
      if (this.props.platformRef === null) {
        return;
      }
      this.send({
        type: "spectatorJoin",
        day: this.props.platformRef.day,
        platformId: this.props.platformRef.platformId,
      });
    };
    socket.onmessage = (event) => {
      if (this.socket !== socket) {
        return;
      }
      this.handleMessage(event.data);
    };
    socket.onclose = () => {
      if (this.socket !== socket) {
        return;
      }
      this.socket = null;
      this.scheduleReconnect();
    };
  };

  disconnect = () => {
    this.clearReconnectTimer();
    const socket = this.socket;
    this.socket = null;
    if (socket !== null) {
      socket.close();
    }
    this.stopClockTicker();
  };

  clearReconnectTimer = () => {
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  };

  // Auto-reconnect con backoff: la pantalla de espectador se cae si el host se duerme/reinicia.
  scheduleReconnect = () => {
    this.clearReconnectTimer();
    if (this.state.status === "rejected") {
      return;
    }
    const delay = Math.min(10000, 1000 * Math.pow(1.6, this.reconnectAttempts));
    this.reconnectAttempts += 1;
    this.setState({ status: "reconnecting" });
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  };

  reconnectNow = () => {
    this.clearReconnectTimer();
    this.reconnectAttempts = 0;
    const socket = this.socket;
    this.socket = null;
    if (socket !== null) {
      try {
        socket.close();
      } catch (_error) {
        /* ignore */
      }
    }
    this.connect();
  };

  send = (message: unknown) => {
    if (this.socket === null || this.socket.readyState !== 1) {
      return;
    }
    this.socket.send(JSON.stringify(message));
  };

  handleMessage = (rawData: unknown) => {
    let message: PlatformLiveServerMessage;
    try {
      message = JSON.parse(String(rawData)) as PlatformLiveServerMessage;
    } catch (_error) {
      return;
    }

    if (message.type === "accepted" && message.role === "spectator") {
      this.reconnectAttempts = 0;
      this.applyPlatformState(message.state);
      return;
    }
    if (message.type === "platformState") {
      this.applyPlatformState(message.state);
      return;
    }
    if (message.type === "rejected") {
      this.setState({ status: "rejected", error: message.reason, ...makeEmptyDisplayState() });
    }
  };

  applyPlatformState = (platformState: PlatformLiveState) => {
    const receivedAtMs = Date.now();
    const serverNowMs = typeof platformState.serverNowMs === "number" ? platformState.serverNowMs : receivedAtMs;
    this.setState({
      status: platformState.currentAttempt === null ? "waiting" : "connected",
      error: null,
      operatorConnected: platformState.operatorConnected,
      currentAttempt: platformState.currentAttempt,
      nextAttempt: platformState.nextAttempt,
      lastResolvedAttempt: platformState.lastResolvedAttempt,
      attemptClock: platformState.attemptClock || null,
      judgeLights: platformState.judgeLights || emptyJudgeLights(),
      manualVerdictConfirmation: platformState.features?.manualVerdictConfirmation === true,
      serverNowMs,
      clockReceivedAtMs: receivedAtMs,
      clockTick: receivedAtMs,
    });

    if (platformState.attemptClock !== null && platformState.attemptClock !== undefined) {
      this.startClockTicker();
    } else if (this.isRevealActiveFor(platformState.lastResolvedAttempt, serverNowMs, receivedAtMs, receivedAtMs)) {
      this.startClockTicker();
    } else {
      this.stopClockTicker();
    }
  };

  startClockTicker = () => {
    if (this.clockTimer !== null) {
      return;
    }
    this.clockTimer = window.setInterval(() => {
      this.setState({ clockTick: Date.now() }, () => {
        if (this.state.attemptClock === null && !this.isRevealActive()) {
          this.stopClockTicker();
        }
      });
    }, 1_000);
  };

  stopClockTicker = () => {
    if (this.clockTimer === null) {
      return;
    }
    window.clearInterval(this.clockTimer);
    this.clockTimer = null;
  };

  getCurrentClockSeconds = (): number | null => {
    const currentAttempt = this.getDisplayCurrentAttempt();
    if (
      this.isRevealActive() ||
      currentAttempt === null ||
      this.state.attemptClock?.attemptKey !== currentAttempt.key
    ) {
      return null;
    }

    return getRemainingClockSeconds(
      {
        attemptClock: this.state.attemptClock,
        serverNowMs: this.state.serverNowMs,
        receivedAtMs: this.state.clockReceivedAtMs,
      },
      this.state.clockTick,
    );
  };

  isRevealActiveFor = (
    lastResolvedAttempt: PlatformLiveResolvedAttempt | null,
    serverNowMs: number | null,
    receivedAtMs: number | null,
    clientNowMs: number,
  ): boolean => {
    if (typeof lastResolvedAttempt?.revealUntilMs !== "number") {
      return false;
    }

    const effectiveServerNowMs = getEffectiveServerNowMs({ serverNowMs, receivedAtMs }, clientNowMs);
    return effectiveServerNowMs !== null && effectiveServerNowMs < lastResolvedAttempt.revealUntilMs;
  };

  isRevealActive = (): boolean => {
    // The audience verdict reveal is a fixed 2s window (RESULT_REVEAL_DURATION_MS) — the operator holding
    // the next lifter does NOT extend it; after 2s the spectator shows "Esperando intento actual" until
    // the operator presses "Siguiente". (The operator keeps the verdict in their own bar meanwhile.)
    return this.isRevealActiveFor(
      this.state.lastResolvedAttempt,
      this.state.serverNowMs,
      this.state.clockReceivedAtMs,
      this.state.clockTick,
    );
  };

  getDisplayCurrentAttempt = (): PlatformLiveAttempt | null => {
    if (this.isRevealActive()) {
      return this.state.lastResolvedAttempt?.attempt || null;
    }
    return this.state.currentAttempt;
  };

  getDisplayNextAttempt = (): PlatformLiveAttempt | null => {
    if (!this.isRevealActive()) {
      return this.state.nextAttempt;
    }

    const currentAttempt = this.state.currentAttempt;
    if (currentAttempt !== null && currentAttempt.key !== this.state.lastResolvedAttempt?.attemptKey) {
      return currentAttempt;
    }
    return this.state.nextAttempt;
  };

  getDisplayJudgeLights = (): JudgeLights => {
    const revealActive = this.isRevealActive();
    if (this.state.manualVerdictConfirmation && this.state.currentAttempt !== null && !revealActive) {
      return emptyJudgeLights();
    }

    let lights: JudgeLights;
    if (revealActive) {
      lights = this.state.lastResolvedAttempt?.judgeLights || this.state.judgeLights;
    } else if (this.state.currentAttempt === null) {
      return emptyJudgeLights();
    } else {
      lights = this.state.judgeLights;
    }

    return judgeLightsShownTogether(lights);
  };

  getLightLabel = (value: JudgeLightValue): string => {
    if (value === "white") {
      return "Blanca";
    }
    if (value === "red") {
      return "Roja";
    }
    return "Apagada";
  };

  getLightClassName = (value: JudgeLightValue): string => {
    if (value === "white") {
      return styles.lightWhite;
    }
    if (value === "red") {
      return styles.lightRed;
    }
    return styles.lightOff;
  };

  getConnectionStatus = (): string => {
    if (this.state.error !== null) {
      return `${this.state.status}: ${this.state.error}`;
    }
    if (!this.state.operatorConnected) {
      return `${this.state.status}: esperando operador`;
    }
    return this.state.status;
  };

  renderJudgeLight = (position: JudgePosition, lights: JudgeLights) => {
    const value = lights[position];
    return (
      <div key={position} className={styles.lightSlot} aria-label={`${position} light ${value}`}>
        <div className={`${styles.light} ${this.getLightClassName(value)}`} aria-label={`${position} light ${value}`} />
      </div>
    );
  };

  renderJudgeLights = () => {
    const lights = this.getDisplayJudgeLights();
    return (
      <section className={styles.lights} aria-label="Judge lights">
        <div className={styles.lightRow}>
          {JUDGE_POSITIONS.map((position) => this.renderJudgeLight(position, lights))}
        </div>
      </section>
    );
  };

  renderInvalid = () => {
    return (
      <div className={styles.spectatorDisplay}>
        <div className={styles.invalid} role="alert">
          <h1>Invalid Spectator URL</h1>
          <p>The requested day and platform do not exist in this meet setup.</p>
        </div>
      </div>
    );
  };

  renderTimer = (remainingSeconds: number | null) => {
    const running = remainingSeconds !== null;
    const seconds = running ? Math.max(0, remainingSeconds) : 60;
    const danger = running && seconds <= 10;
    const radius = 100;
    const circumference = 2 * Math.PI * radius;
    const progress = Math.max(0, Math.min(1, seconds / 60));
    return (
      <section
        className={`${styles.timer} ${danger ? styles.timerDanger : ""}`}
        aria-label={danger ? "Attempt Clock danger" : "Attempt Clock"}
      >
        <svg className={styles.clockFace} viewBox="0 0 240 240" aria-hidden="true">
          <circle className={styles.clockTrack} cx="120" cy="120" r={radius} />
          <circle
            className={styles.clockProgress}
            cx="120"
            cy="120"
            r={radius}
            pathLength={circumference}
            strokeDasharray={circumference}
            style={{ strokeDashoffset: circumference * (1 - progress) }}
            aria-label="Attempt Clock progress"
          />
        </svg>
        <div className={styles.timerValue} aria-label="Attempt Clock value">
          {seconds}
        </div>
      </section>
    );
  };

  renderRecordBadge = (attempt: PlatformLiveAttempt | null, className?: string) => {
    if (attempt?.recordAttempt !== true) {
      return null;
    }
    const label = recordAttemptLabel(attempt.recordCategory);
    return (
      <span
        className={`${styles.recordBadge} ${className || ""}`}
        aria-label={recordAttemptAriaLabel(attempt.recordCategory)}
        {...recordCategoryDataAttributes(attempt.recordCategory)}
      >
        {label}
      </span>
    );
  };

  renderBarLoad = (
    attempt: PlatformLiveAttempt | null,
    label: "PESO ACTUAL" | "PESO SIGUIENTE",
    ariaLabel: "Bar load" | "Next bar load",
    placement: "hero" | "corner",
  ) => {
    if (attempt === null) {
      return null;
    }

    const isRecord = attempt.recordAttempt === true;
    const barLoad = attempt.barLoad;
    const inHero = placement === "hero";
    return (
      <section
        className={`${styles.barLoadPanel} ${inHero ? styles.barLoadPanelHero : ""} ${
          isRecord ? styles.barLoadPanelRecord : ""
        }`}
        aria-label={ariaLabel}
        {...(isRecord ? recordCategoryDataAttributes(attempt.recordCategory) : {})}
      >
        <div className={`${styles.barLoadGraphic} ${isRecord ? styles.barLoadGraphicRecord : ""}`}>
          {barLoad === undefined ? null : (
            <BarLoadView
              lift={attempt.lift}
              loading={barLoad.loading}
              rackInfo={barLoad.rackInfo}
              inKg={barLoad.inKg}
              language={this.props.language}
            />
          )}
        </div>
        <div className={styles.weightReadout}>
          <span className={styles.eyebrow}>{label}</span>
          {/* Arriba del nombre el récord ya tiene su propio cartel. */}
          {inHero ? null : this.renderRecordBadge(attempt)}
          <div className={styles.weightValue}>
            <span>{attempt.weightKg.toFixed(1)}</span>
            <small>kg</small>
          </div>
        </div>
      </section>
    );
  };

  renderAttemptHistory = (attempt: PlatformLiveAttempt | null) => {
    if (attempt === null) {
      return null;
    }

    return (
      <div className={styles.attemptHistoryWrap}>
        <SpectatorLiftProgress
          attempt={attempt}
          revealActive={this.isRevealActive()}
          revealAttemptKey={this.state.lastResolvedAttempt?.attemptKey || null}
          revealedResult={this.state.lastResolvedAttempt?.result || null}
        />
      </div>
    );
  };

  renderAthleteCapsule = (attempt: PlatformLiveAttempt | null, hidden = false) => {
    if (attempt === null) {
      return (
        <span className={styles.capsule} aria-hidden={hidden || undefined}>
          Día {this.props.platformRef?.day} <span>•</span> Plataforma {this.props.platformRef?.platformId}
        </span>
      );
    }

    const meta = attempt.athleteMeta;
    const ageDivision = meta?.ageDivision?.trim() || meta?.category.split(",")[0].trim() || null;
    const age = meta !== undefined && meta.age > 0 ? `${meta.age} AÑOS` : null;
    const equipment = meta?.equipment === undefined ? null : this.getEquipmentDivisionLabel(meta.equipment);
    const weightClass = this.getBodyweightCategoryLabel(meta?.weightClassKg);
    const details = [ageDivision, age, equipment, weightClass].filter((detail): detail is string => detail !== null);
    return (
      <span className={styles.capsule} aria-label="Athlete details" aria-hidden={hidden || undefined}>
        {details.join(" • ")}
      </span>
    );
  };

  getEquipmentDivisionLabel = (equipment: Equipment): string => {
    if (equipment === "Bare") return "RAW";
    if (equipment === "Sleeves") return "RODILLERAS";
    if (equipment === "Wraps") return "VENDAS";
    return equipment.toUpperCase();
  };

  getBodyweightCategoryLabel = (weightClassKg: string | undefined): string | null => {
    const value = weightClassKg?.trim().replace(/\s*KG$/i, "");
    if (!value) return null;
    if (value.endsWith("+")) return `+${value.slice(0, -1)} KG`;
    return `${value.startsWith("-") || value.startsWith("+") ? value : `-${value}`} KG`;
  };

  renderDecisionBanner = () => {
    if (!this.isRevealActive()) {
      return null;
    }
    const result = this.state.lastResolvedAttempt?.result;
    if (result !== "good" && result !== "noLift") {
      return null;
    }
    const valid = result === "good";
    return (
      <>
        <div
          className={`${styles.decisionFrame} ${valid ? styles.decisionFrameValid : styles.decisionFrameNoLift}`}
          aria-hidden="true"
        />
        <div className={styles.decisionBadge}>
          <div
            className={valid ? styles.validBanner : styles.noLiftBanner}
            aria-label={valid ? "Intento válido" : "Intento nulo"}
          >
            <span>Intento</span>
            <strong>{valid ? "VÁLIDO" : "NULO"}</strong>
          </div>
        </div>
      </>
    );
  };

  render() {
    if (this.props.platformRef === null) {
      return this.renderInvalid();
    }

    const remainingSeconds = this.getCurrentClockSeconds();
    const currentAttempt = this.getDisplayCurrentAttempt();
    const nextAttempt = this.getDisplayNextAttempt();
    return (
      <main className={styles.spectatorDisplay}>
        <div className={styles.spectatorCanvas} style={this.getCanvasStyle()} aria-label="Spectator Display Canvas">
          <img className={styles.background} src={spectatorBackground} alt="" aria-hidden="true" />
          {this.renderJudgeLights()}

          <section
            className={`${styles.hero} ${currentAttempt === null ? styles.heroWaiting : ""}`}
            aria-label="Intento actual"
            {...(currentAttempt?.recordAttempt === true
              ? recordCategoryDataAttributes(currentAttempt.recordCategory)
              : {})}
          >
            {/* La barra actual va arriba del nombre, donde la miran los cargadores, y toma el alto que quede libre. */}
            {currentAttempt === null ? null : (
              <FitToHeight
                className={styles.heroBarLoad}
                naturalHeight={BAR_LOAD_GRAPHIC_HEIGHT_PX}
                minScale={0.45}
                maxScale={1}
              >
                {this.renderBarLoad(currentAttempt, "PESO ACTUAL", "Bar load", "hero")}
              </FitToHeight>
            )}
            {/* El resplandor de récord envuelve solo la identidad, no el progreso que va debajo. */}
            <div
              className={`${styles.heroIdentity} ${currentAttempt?.recordAttempt === true ? styles.heroRecord : ""}`}
            >
              {currentAttempt === null ? (
                <h1>
                  {this.state.lastResolvedAttempt != null
                    ? "Esperando al siguiente atleta"
                    : "Esperando intento actual"}
                </h1>
              ) : (
                <>
                  {this.renderRecordBadge(currentAttempt, styles.heroRecordBadge)}
                  <h1>
                    {/* Una línea achicando; dos solo para nombres extremos. La key refita si cambia el ancho. */}
                    <FitLine
                      key={this.state.viewportWidth / this.state.viewportHeight}
                      maxFontSize={100}
                      singleLineMinFontSize={44}
                      wrapMaxFontSize={44}
                      minFontSize={36}
                      maxLines={2}
                    >
                      {currentAttempt.lifterName}
                    </FitLine>
                  </h1>
                </>
              )}
              {this.renderAthleteCapsule(currentAttempt)}
            </div>
            {this.renderAttemptHistory(currentAttempt)}
          </section>

          <div className={styles.barLoads} aria-label="Peso siguiente">
            {this.renderBarLoad(nextAttempt, "PESO SIGUIENTE", "Next bar load", "corner")}
          </div>
          <div className={styles.timerPosition} aria-label="Temporizador">
            {this.renderTimer(remainingSeconds)}
          </div>
          <div className={styles.sponsorsRail} aria-label="Sponsors del torneo">
            <img className={styles.sponsors} src={sponsors} alt="Sponsors del torneo" />
          </div>
          <div className={styles.connectionStatus} aria-label="Spectator Connection Status">
            {this.getConnectionStatus()}
          </div>
          {this.renderDecisionBanner()}
        </div>
      </main>
    );
  }
}

export default SpectatorView;
